import { MarkdownContent } from "@/app/components/Markdown";
import {
  CheckIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/16/solid";
import { QuestionDTO } from "@onetutor/shared/dto";
import { QuestionDifficulty, QuestionType } from "@onetutor/shared/type";
import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { openModal } from "../../components/modal";
import { useToastEffect } from "../../hooks";
import { usePatchQuestionMutation } from "../../services/quizApi";
interface EditQuestionCardProps {
  question: QuestionDTO;
  index: number;
  setQuestionToEdit: React.Dispatch<
    React.SetStateAction<QuestionDTO | undefined>
  >;
  isEditPage: boolean;
  modalIdDelete: string;
  modalIdEdit: string;
  setResetEditMask: React.Dispatch<React.SetStateAction<boolean>>;
  setShouldFetch: React.Dispatch<React.SetStateAction<boolean>>;
  setQuestionsListParent?: React.Dispatch<React.SetStateAction<QuestionDTO[]>>;
}

export function EditQuestionCard({
  question,
  index,
  setQuestionToEdit,
  isEditPage,
  modalIdDelete,
  modalIdEdit,
  setResetEditMask,
  setShouldFetch,
  setQuestionsListParent,
}: EditQuestionCardProps) {
  const { t } = useTranslation("manageQuizPage");

  const [patchQuestion, { isError, error }] = usePatchQuestionMutation();

  const { courseId } = useParams();
  if (!courseId) {
    throw new Error("Course ID is required");
  }

  useToastEffect({
    isError,
    error,
  });

  return (
    <div
      key={question.id}
      className={`${!question.isPublished && isEditPage ? "bg-base-300" : "bg-neutral"} my-4 rounded-lg p-md shadow-sm`}
    >
      <div className="flex flex-row items-center justify-between">
        <span className="font-semibold">
          {t("questionIndex")}: {index + 1}
        </span>
        <div className="flex flex-row items-center justify-end">
          {isEditPage && (
            <div
              className="tooltip"
              data-tip={
                question.isPublished
                  ? t("tooltipUnpublishQuestion")
                  : t("tooltipPublishQuestion")
              }
            >
              <button
                type="button"
                className="btn btn-ghost btn-sm btn-circle"
                onClick={() => {
                  patchQuestion({
                    courseId,
                    questionId: question.id,
                    body: { isPublished: !question.isPublished },
                  });

                  if (setQuestionsListParent) {
                    setQuestionsListParent((prev) => {
                      return prev.map((q) => {
                        if (q.id === question.id) {
                          return { ...q, isPublished: !q.isPublished };
                        }

                        return q;
                      });
                    });
                  }

                  setShouldFetch(false);
                }}
              >
                {question.isPublished ? (
                  <EyeIcon className="h-6 w-6 cursor-pointer" />
                ) : (
                  <EyeSlashIcon className="h-6 w-6 cursor-pointer" />
                )}
              </button>
            </div>
          )}

          <div
            className={`badge badge-accent badge-md ml-md border-2 ${
              question.difficulty === QuestionDifficulty.EASY
                ? "border-success bg-success"
                : question.difficulty === QuestionDifficulty.MEDIUM
                  ? "border-warning bg-warning"
                  : "border-error bg-error"
            }`}
          >
            {t(`difficultyOptions.${question.difficulty}`)}
          </div>
          <div className="badge badge-md ml-sm border-2 border-neutral-content bg-transparent ">
            {question.type === QuestionType.MULTIPLE_CHOICE
              ? t("multipleChoice")
              : t("freeText")}
          </div>
          <PencilSquareIcon
            className="ml-sm h-6 w-6 cursor-pointer"
            onClick={() => {
              setQuestionToEdit(question);
              openModal(modalIdEdit, () =>
                setResetEditMask((prevVale) => !prevVale),
              );
            }}
          />

          <TrashIcon
            className="mb-1 ml-md h-6 w-6 cursor-pointer text-error"
            onClick={() => {
              setQuestionToEdit(question);
              openModal(modalIdDelete);
            }}
          />
        </div>
      </div>
      <div className="font-bold">
        <MarkdownContent>{question.question}</MarkdownContent>
      </div>
      {question.tip && (
        <div className="text-md italic">
          <MarkdownContent>{`${t("tip")}: ${question.tip}`}</MarkdownContent>
        </div>
      )}
      {question.type === QuestionType.FREE_TEXT && (
        <div>
          <div className="my-sm italic underline">
            {t("correctAnswerFreeText")}
          </div>
          <div className="font-semibold">
            <MarkdownContent>{question.answers[0].answer}</MarkdownContent>
          </div>
        </div>
      )}
      {question.type === QuestionType.MULTIPLE_CHOICE &&
        question.answers.map((answer) => (
          <div key={answer.id} className="ms-1 mt-1 flex">
            {answer.isCorrect ? (
              <CheckIcon className="h-6 w-6 text-success" />
            ) : (
              <XMarkIcon className="h-6 w-6 text-error" />
            )}
            <MarkdownContent>{answer.answer}</MarkdownContent>
          </div>
        ))}
    </div>
  );
}
